import React, { useEffect, useRef } from 'react';

interface AnimatedLogoProps {
  size?: number;
  className?: string;
  style?: React.CSSProperties;
}

const AnimatedLogo: React.FC<AnimatedLogoProps> = ({ 
  size = 80, 
  className = '', 
  style = {} 
}) => {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    // Create the animation using CSS animations
    const styleElement = document.createElement('style');
    styleElement.id = 'clouseau-logo-animations';
    
    // Check if style already exists to avoid duplicates
    if (!document.getElementById('clouseau-logo-animations')) {
      styleElement.textContent = `
        @keyframes spin-clockwise {
          from { transform: rotate(0deg); }
          to { transform: rotate(720deg); }
        }
        
        @keyframes spin-counter-clockwise {
          from { transform: rotate(0deg); }
          to { transform: rotate(-1080deg); }
        }
        
        @keyframes spin-fast-clockwise {
          from { transform: rotate(0deg); }
          to { transform: rotate(1440deg); }
        }
        
        .animate-group-1 {
          animation: spin-clockwise 14s linear infinite;
          transform-origin: 13.28px 13.28px;
        }
        
        .animate-group-2 {
          animation: spin-counter-clockwise 14s linear infinite;
          transform-origin: 13.25px 13.25px;
        }
        
        .animate-group-3 {
          animation: spin-fast-clockwise 14s linear infinite;
          transform-origin: 13.275px 13.28px;
        }
      `;
      
      document.head.appendChild(styleElement);
    }
    
    // Apply animation classes to the groups with proper type assertion
    const group3 = svg.querySelector('#eBcV8EvPGdn3') as SVGElement | null;
    const group6 = svg.querySelector('#eBcV8EvPGdn6') as SVGElement | null;
    const group7 = svg.querySelector('#eBcV8EvPGdn7') as SVGElement | null;
    const group10 = svg.querySelector('#eBcV8EvPGdn10') as SVGElement | null;
    const group13 = svg.querySelector('#eBcV8EvPGdn13') as SVGElement | null;
    const group16 = svg.querySelector('#eBcV8EvPGdn16') as SVGElement | null;
    const group17 = svg.querySelector('#eBcV8EvPGdn17') as SVGElement | null;
    
    if (group3) group3.classList.add('animate-group-1');
    if (group6) group6.classList.add('animate-group-1');
    if (group7) group7.classList.add('animate-group-2');
    if (group10) group10.classList.add('animate-group-2');
    if (group13) group13.classList.add('animate-group-2');
    if (group16) group16.classList.add('animate-group-3');
    if (group17) group17.classList.add('animate-group-3');
    
    return () => {
      // Cleanup is optional since we only add the style once
    };
  }, []);

  return (
    <svg
      ref={svgRef}
      id="clouseau-animated-logo"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 26.55 26.55"
      width={size}
      height={size}
      className={`logo-image ${className}`}
      style={{ 
        opacity: 0.8, 
        cursor: 'grab', 
        transition: 'transform 0.3s ease', 
        ...style 
      }}
      shapeRendering="geometricPrecision"
      textRendering="geometricPrecision"
    >
      <path 
        d="M15.75,15.24c-.58.73-1.47,1.19-2.47,1.19-1.75,0-3.16-1.42-3.16-3.16s1.42-3.16,3.16-3.16c1.01,0,1.91.47,2.49,1.21" 
        fill="none" 
        stroke="#40403f" 
        strokeWidth="1.5" 
        strokeLinecap="round" 
        strokeMiterlimit="10"
      />
      
      <g id="eBcV8EvPGdn3" transform="translate(.01 0)">
        <path 
          d="M13.27,19.63c-3.51,0-6.35-2.85-6.35-6.35c0-1.81.76-3.44,1.97-4.6" 
          fill="none" 
          stroke="#40403f" 
          strokeWidth="1.5" 
          strokeLinecap="round" 
          strokeMiterlimit="10"
        />
        <line 
          x1="5.21" 
          y1="16.93" 
          x2="7.02" 
          y2="16.04" 
          fill="none" 
          stroke="#40403f" 
          strokeWidth="1.5" 
          strokeLinecap="round" 
          strokeMiterlimit="10"
        />
      </g>
      
      <path 
        id="eBcV8EvPGdn6" 
        d="M11.62,7.14c.53-.14,1.08-.22,1.65-.22c1.1,0,2.13.28,3.03.77.77.42,1.45.99,1.98,1.68.84,1.08,1.34,2.43,1.34,3.91c0,2.34-1.27,4.39-3.15,5.49" 
        fill="none" 
        stroke="#40403f" 
        strokeWidth="1.5" 
        strokeLinecap="round" 
        strokeMiterlimit="10"
      />
      
      <g id="eBcV8EvPGdn7" transform="translate(-.030888-.020879)">
        <path 
          d="M4.09,11.06c1-4.15,4.73-7.23,9.19-7.23c1.12,0,2.2.2,3.2.56" 
          fill="none" 
          stroke="#40403f" 
          strokeWidth="1.5" 
          strokeLinecap="round" 
          strokeMiterlimit="10"
        />
        <line 
          x1="4.53" 
          y1="5.02" 
          x2="6" 
          y2="6.4" 
          fill="none" 
          stroke="#40403f" 
          strokeWidth="1.5" 
          strokeLinecap="round" 
          strokeMiterlimit="10"
        />
      </g>
      
      <g id="eBcV8EvPGdn10" transform="translate(.01 0)">
        <path 
          d="M19.3,6c2.04,1.7,3.36,4.24,3.42,7.09" 
          fill="none" 
          stroke="#40403f" 
          strokeWidth="1.5" 
          strokeLinecap="round" 
          strokeMiterlimit="10"
        />
        <line 
          x1="24.92" 
          y1="10.25" 
          x2="22.97" 
          y2="10.73" 
          fill="none" 
          stroke="#40403f" 
          strokeWidth="1.5" 
          strokeLinecap="round" 
          strokeMiterlimit="10"
        />
      </g>
      
      <g id="eBcV8EvPGdn13" transform="translate(-.035888-.030888)">
        <path 
          d="M22.35,15.9c-1.14,3.94-4.77,6.82-9.08,6.82-5.01,0-9.11-3.9-9.43-8.82" 
          fill="none" 
          stroke="#40403f" 
          strokeWidth="1.5" 
          strokeLinecap="round" 
          strokeMiterlimit="10"
        />
        <line 
          x1="19.9" 
          y1="18.62" 
          x2="18.29" 
          y2="17.42" 
          fill="none" 
          stroke="#40403f" 
          strokeWidth="1.5" 
          strokeLinecap="round" 
          strokeMiterlimit="10"
        />
      </g>
      
      <path 
        id="eBcV8EvPGdn16" 
        d="M4.46,22.17c-2.29-2.27-3.71-5.42-3.71-8.9c0-5.37,3.39-9.96,8.14-11.73" 
        transform="translate(-.005 0)" 
        fill="none" 
        stroke="#40403f" 
        strokeWidth="1.5" 
        strokeLinecap="round" 
        strokeMiterlimit="10"
      />
      
      <g id="eBcV8EvPGdn17" transform="translate(-.005 0)">
        <path 
          d="M11.92,0.82c.44-.05.9-.07,1.35-.07c6.92,0,12.53,5.61,12.53,12.53s-5.61,12.53-12.53,12.53c-2.51,0-4.85-.74-6.81-2.01" 
          fill="none" 
          stroke="#40403f" 
          strokeWidth="1.5" 
          strokeLinecap="round" 
          strokeMiterlimit="10"
        />
        <line 
          x1="10.35" 
          y1="24.77" 
          x2="10.75" 
          y2="22.8" 
          fill="none" 
          stroke="#40403f" 
          strokeWidth="1.5" 
          strokeLinecap="round" 
          strokeMiterlimit="10"
        />
      </g>
    </svg>
  );
};

export default AnimatedLogo;